/**
 * Students service — Firestore CRUD for the `students` collection.
 *
 * A student document:
 *   {
 *     name, email, grade, parentName, parentEmail, country, timezone,
 *     parentTimezone,           // parent's own tz for emails; '' = same as `timezone`
 *     mentorIds,                // string[] — every mentor this student learns with
 *     mentorEmails,             // string[] — the same mentors, lowercased emails
 *     mentorId,                 // derived primary mentor (first active course)
 *     createdAt, updatedAt      // server timestamps
 *   }
 *
 * These are platform-managed records distinct from the auth `users`
 * collection — an admin curates them whether or not the person has logged in.
 *
 * The three mentor fields are a DERIVED snapshot of the student's enrollments
 * and classes — a student learns several courses at once, each with its own
 * mentor. The server's mentor-link triggers own them: never set them from a form.
 */
import firestoreService, { orderBy, where, limit } from './firestore.service';

const STUDENTS = 'students';

/**
 * Whitelist the fields we persist so stray form state never leaks in.
 * Deliberately excludes the derived mentor snapshot (`mentorIds`,
 * `mentorEmails`, `mentorId`) — updates are partial, so an admin editing a
 * student's grade can never clobber the server-derived links.
 */
function sanitize(input = {}) {
  return {
    name: (input.name || '').trim(),
    email: (input.email || '').trim().toLowerCase(),
    grade: input.grade || '',
    parentName: (input.parentName || '').trim(),
    parentEmail: (input.parentEmail || '').trim().toLowerCase(),
    country: input.country || '',
    timezone: input.timezone || '',
    parentTimezone: input.parentTimezone || '',
  };
}

export const studentsService = {
  /** Realtime subscription to all students, newest first. Returns unsubscribe. */
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(
      STUDENTS,
      [orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  list() {
    return firestoreService.getCollection(STUDENTS, [orderBy('createdAt', 'desc')]);
  },

  /** Server-side total count (aggregation — no document downloads). */
  count() {
    return firestoreService.count(STUDENTS);
  },

  /**
   * Realtime subscription to every student a mentor teaches — across all of
   * their courses, not just a single "assigned mentor" pairing.
   *
   * Filtered server-side by `mentorEmails array-contains <their email>`, which
   * is exactly the predicate the security rule matches on, so the query is
   * provably allowed (see functions/src/scheduling/mentorLinks.js). Sorted by name
   * client-side to avoid a composite index. Powers every mentor view that needs
   * a student's name.
   *
   * @param {string} email  the mentor's own (lowercased) email
   */
  subscribeByMentorEmail(email, onData, onError) {
    const key = (email || '').trim().toLowerCase();
    if (!key) {
      onData([]);
      return () => {};
    }
    return firestoreService.subscribeCollection(
      STUDENTS,
      [where('mentorEmails', 'array-contains', key)],
      (rows) => onData([...rows].sort((a, b) => (a.name || '').localeCompare(b.name || ''))),
      onError
    );
  },

  /**
   * Resolve the student record for a signed-in student user by their email — the
   * link between an auth account (`users/{uid}`) and the admin-curated student
   * profile. Returns the matching record or null if the admin hasn't enrolled
   * this student yet. Emails are stored lowercased, so callers should lowercase.
   */
  async getByEmail(email) {
    if (!email) return null;
    const matches = await firestoreService.getCollection(STUDENTS, [
      where('email', '==', email.trim().toLowerCase()),
      limit(1),
    ]);
    return matches[0] || null;
  },

  get(id) {
    return firestoreService.getDocument(STUDENTS, id);
  },

  create(data) {
    return firestoreService.addDocument(STUDENTS, {
      ...sanitize(data),
      // Seeded empty so the mentor's array-contains query never skips the doc
      // for a missing field; the link trigger fills them in on first link.
      mentorIds: [],
      mentorEmails: [],
      mentorId: '',
    });
  },

  update(id, data) {
    return firestoreService.updateDocument(STUDENTS, id, sanitize(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(STUDENTS, id);
  },
};

export default studentsService;
